import React from "react"
import {useTranslation} from "react-i18next"
import {ArrowTrendingDownIcon} from "@heroicons/react/20/solid"
import ArrowTrendingUpIcon from "@heroicons/react/20/solid/ArrowTrendingUpIcon"
import {InformationCircleIcon} from "@heroicons/react/24/outline"
import {twMerge} from "tailwind-merge"

import {GenericErrorAlert} from "../../components/Alert.tsx"
import {BoardChart} from "../../components/BoardChart.tsx"
import {IconButton} from "../../components/Button.tsx"
import {Loading} from "../../components/Loading.tsx"
import {Tooltip} from "../../components/Tooltip.tsx"
import {useSaaSDashboardSalesIntelligenceQuery} from "../../queries/salesPlans.ts"
import {createChartData} from "./utils.ts"

export const Statistics: React.FC<any> = props => {
  const {data, error, refetch} = useSaaSDashboardSalesIntelligenceQuery(props.startDate)
  const {t} = useTranslation()

  if (error) {
    return <GenericErrorAlert retry={refetch} />
  }
  if (!data) {
    return <Loading />
  }

  const salesIntelligence = data

  // Configuration for statistics
  const statisticsConfig = [
    {
      id: "crm-utilization",
      title: t("Dashboard_SaaS_SalesIntelligence_CrmUtilization"),
      tooltipText: t("Dashboard_SaaS_SalesIntelligence_CrmUtilizationTooltip"),
      percentage: salesIntelligence?.crm_utilization_ratio,
      changePercentage: salesIntelligence?.crm_utilization_change,
      chartData: createChartData(
        salesIntelligence?.crm_utilization_two_month_ago_ratio ?? 0,
        salesIntelligence?.crm_utilization_one_month_ago_ratio ?? 0,
        salesIntelligence?.crm_utilization_ratio ?? 0
      ),
    },
    {
      id: "rotten-deals",
      title: t("Dashboard_SaaS_SalesIntelligence_RottenDeals"),
      tooltipText: t("Dashboard_SaaS_SalesIntelligence_RottenDealsTooltip"),
      percentage: salesIntelligence?.rotten_deals_ratio,
      changePercentage: salesIntelligence?.rotten_deals_change,
      invertPositive: true, // Lower is better for rotten deals
      chartData: createChartData(
        salesIntelligence?.rotten_deals_two_month_ago_ratio ?? 0,
        salesIntelligence?.rotten_deals_one_month_ago_ratio ?? 0,
        salesIntelligence?.rotten_deals_ratio ?? 0
      ),
    },
    {
      id: "deals-with-activity",
      title: t("Dashboard_SaaS_SalesIntelligence_DealsWithActivity"),
      tooltipText: t("Dashboard_SaaS_SalesIntelligence_DealsWithActivityTooltip"),
      percentage: salesIntelligence?.deals_with_plan_ratio,
      changePercentage: salesIntelligence?.deals_with_plan_change,
      chartData: createChartData(
        salesIntelligence?.deals_with_plan_two_month_ago_ratio ?? 0,
        salesIntelligence?.deals_with_plan_one_month_ago_ratio ?? 0,
        salesIntelligence?.deals_with_plan_ratio ?? 0
      ),
    },
  ]

  return (
    <>
      {statisticsConfig.map(config => (
        <Statistic
          key={config.id}
          title={config.title}
          percentage={config.percentage ?? null}
          changePercentage={config.changePercentage ?? null}
          invertPositive={config.invertPositive}
          chartData={config.chartData}
          tooltipText={config.tooltipText}
        />
      ))}
    </>
  )
}

export const Statistic: React.FC<{
  title: string
  tooltipText: string
  percentage: null | number
  changePercentage: null | number
  invertPositive?: boolean
  chartData?: any[]
}> = ({title, tooltipText, percentage, changePercentage, invertPositive = false, chartData}) => {
  const {t} = useTranslation()
  const hasNoChange = changePercentage === null
  const displayValue = percentage ?? "--"
  const changeValue = changePercentage === null ? "--" : Math.abs(changePercentage)

  // Calculate if the change is positive based on the value and whether to invert the logic
  const isPositive = hasNoChange ? false : invertPositive ? (changePercentage ?? 0) <= 0 : (changePercentage ?? 0) >= 0

  const changeColorClass = hasNoChange ? "text-cr-grey-50" : isPositive ? "text-cr-green" : "text-cr-red"

  const chartColor = hasNoChange ? "#808080" : isPositive ? "#01a934" : "#E92323"

  return (
    <div
      className={"flex flex-1 flex-row items-center justify-between gap-4 card-shadow rounded-lg bg-white px-6 py-5"}
    >
      <div className={"flex flex-col gap-4"}>
        <div className={"flex gap-1"}>
          <h2 className={"text-lg text-cr-grey-65"}>{title}</h2>
          <Tooltip
            buttonNode={
              <IconButton className={"p-2"} noEdges>
                <InformationCircleIcon className={"h-5 w-5 flex-shrink-0 text-cr-grey-50"} />
              </IconButton>
            }
            openDelay={200}
          >
            <div className={"rounded-lg bg-gray-900/90 px-2 py-2 text-sm text-cr-white"}>{tooltipText}</div>
          </Tooltip>
        </div>
        <span className={"text-3xl font-bold text-cr-black"}>{displayValue}%</span>
      </div>

      <div className={"flex flex-1 flex-col items-end justify-between"}>
        <div className={twMerge("flex items-center gap-1 text-sm", changeColorClass)}>
          {(changePercentage ?? 0) >= 0 ? (
            <ArrowTrendingUpIcon className={"h-4 w-4"} />
          ) : (
            <ArrowTrendingDownIcon className={"h-4 w-4"} />
          )}
          <span>{changeValue}%</span>
        </div>

        <div className={"h-14 w-[135px]"}>
          <BoardChart data={chartData} color={chartColor} />
        </div>

        <span className={"text-xs text-cr-grey-30"}>{t("Dashboard_SaaS_SalesIntelligence_LastThreeMonths")}</span>
      </div>
    </div>
  )
}
